import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Loader2, Send } from 'lucide-react';
import CommitmentTaskBadge from './CommitmentTaskBadge';
import type { CommitmentTaskType, RewardMethodType } from '@clareefai/types';

interface TaskSelection {
  type: CommitmentTaskType;
  requiredCount: number;
}

interface PreviewStepProps {
  email: string;
  promoterName: string | null;
  rewardMethod: RewardMethodType;
  giftCardAmount: number;
  giftCardCurrency: string;
  discountUnit: string;
  discountAmount: number;
  additionalNote: string;
  selectedTasks: TaskSelection[];
  deadline: string;
  invitationMessage: string;
  onInvitationMessageChange: (msg: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const getDiscountLabel = (unit: string, amount: number, tFn: TFn) => {
  switch (unit) {
    case 'percentage': return tFn('rewards.specific.detail.rewardUnits.percentage', { amount });
    case 'credits': return tFn('rewards.specific.detail.rewardUnits.credits', { amount });
    case 'free_months': return tFn('rewards.specific.detail.rewardUnits.freeMonths', { amount });
    default: return `${amount} ${unit}`;
  }
};

const getRewardLabel = (method: RewardMethodType, giftCardAmount: number, giftCardCurrency: string, discountUnit: string, discountAmount: number, tFn: TFn) => {
  const parts: string[] = [];
  if ((method === 'GIFT_CARD' || method === 'BOTH') && giftCardAmount > 0) {
    parts.push(`${giftCardAmount}${CURRENCY_SYMBOLS[giftCardCurrency] || giftCardCurrency} ${tFn('rewards.specific.preview.giftCard')}`);
  }
  if ((method === 'DISCOUNT' || method === 'BOTH') && discountAmount > 0) {
    parts.push(getDiscountLabel(discountUnit, discountAmount, tFn));
  }
  return parts.join(` ${tFn('rewards.or')} `) || tFn('rewards.specific.preview.noReward');
};

const PreviewStep: React.FC<PreviewStepProps> = ({
  email, promoterName, rewardMethod, giftCardAmount, giftCardCurrency,
  discountUnit, discountAmount, additionalNote,
  selectedTasks, deadline, invitationMessage, onInvitationMessageChange,
  onBack, onCancel, onSubmit, isSubmitting,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('rewards.specific.preview.title')}</h3>
        <p className="text-gray-500">{t('rewards.specific.preview.subtitle')}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Summary Card */}
        <div className="bg-gradient-to-br from-[#4339A0]/5 to-indigo-50 border border-[#4339A0]/20 rounded-xl p-6 space-y-4">
          <h4 className="font-semibold text-gray-900">{t('rewards.specific.preview.commitmentSummary')}</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">{t('rewards.specific.preview.contact')}</p>
              <p className="font-medium text-gray-900">{promoterName || email}</p>
              {promoterName && <p className="text-xs text-gray-500">{email}</p>}
            </div>
            <div>
              <p className="text-gray-500">{t('common.rewards')}</p>
              <p className="font-medium text-gray-900">
                {getRewardLabel(rewardMethod, giftCardAmount, giftCardCurrency, discountUnit, discountAmount, t)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">{t('rewards.specific.detail.dueDate')}</p>
              <p className="font-medium text-gray-900">{new Date(deadline + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
            {additionalNote && (
              <div>
                <p className="text-gray-500">{t('rewards.specific.detail.note')}</p>
                <p className="font-medium text-gray-900 text-xs">{additionalNote}</p>
              </div>
            )}
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-2">{t('rewards.specific.preview.requiredTasks')}</p>
            <div className="flex flex-wrap gap-2">
              {selectedTasks.map((task) => (
                <CommitmentTaskBadge key={task.type} type={task.type} requiredCount={task.requiredCount} />
              ))}
            </div>
          </div>
        </div>

        {/* Invitation Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.preview.invitationMessage')}</label>
          <p className="text-xs text-gray-500 mb-2">{t('rewards.specific.preview.invitationMessageHint')}</p>
          <textarea
            value={invitationMessage}
            onChange={(e) => onInvitationMessageChange(e.target.value)}
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
            placeholder={t('rewards.specific.preview.invitationMessagePlaceholder')}
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{t('rewards.specific.preview.characters', { count: invitationMessage.length })}</p>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="flex items-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back')}
        </button>
        <div className="flex gap-3">
          <button onClick={onCancel} className="px-6 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
            {t('common.cancel')}
          </button>
          <button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-[#4339A0] hover:bg-[#3d2f8f] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {isSubmitting ? t('rewards.specific.creating') : t('rewards.specific.preview.createAndSend')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewStep;
